/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 * Inquiry answer form container component
 *
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Alexander Golshtein
 * @author Viacheslav Smalenski
 * @version 1.28.0
 * @since 1.15.0
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import { Formik, FormikProps } from 'formik';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';

import { LinkBlock } from '@components/common';
import FormFieldTextarea from '@modules/form/components/FormFieldTextarea';
import EFormValidation from '@modules/form/const/formValidation';
import { TFileError } from '@modules/inquiry/types';
import { isFailedFileAttach, isSuccessFileAttach } from '@modules/inquiry/util/validation';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import type { IPageInquiryInfoTranslation } from '@modules/internationalization/types/pages/inquiryInfo';
import { Loader } from '@modules/loader/components/Loader';
import { useModalOpenError } from '@modules/modal/hooks/useModalOpenError';
import ButtonServiceUserDenied from '@modules/role/components/ButtonServiceUserDenied';
import {
    makeSelectAuthSystemFileUploadLimits,
} from '@pages/PageAuth/selectors/authSelectors';
import { InquiryInfoPageChatFormAttachedFiles } from '@pages/PageInquiryInfo/components/InquiryInfoPageChatFormAttachedFiles';
import { getGlobalInputErrorModalTitle } from '@pages/PageInquiryInfo/utils/getGlobalInputErrorTitle';
import { fontSizeSmall } from '@theme/default/global';
import { getFileExtension } from '@utils/formatText';
import { stringFormatParams } from '@utils/stringFormat';

import {
    useDeleteInquiryDetachFileMutation,
    useLazyGetInquiryInfoGeneralQuery,
    useLazyPostInquiryAnswerQuery,
    usePostInquiryAttachFileMutation,
} from '../store';
import type { IInquiryInfoNewFile } from '../types';
import { EInquiryChatNoteData, InquiryInfoNoteData } from '../types/inquiryInfoType';
import { getAttachFileErrorMessage } from '../utils/getAttachFileErrorMessage';
import {
    ErrorItem,
    InquiryInfoAnswerFooterStyled,
    InquiryInfoAnswerNoteStyled,
    InquiryInfoPageChatHistoryAttachButtonBlockStyled,
    InquiryInfoPageFormContainer,
} from './styled/InquiryInfoPageStyled';

export interface IInquiryInfoPageApproveForm {
    inquiryId: string;
    noteMaxLength: number;
    setIsShowLoader: (isShowLoader: boolean) => void;
    translationInquiryInfo: IPageInquiryInfoTranslation;
    allowedFileExtensions?: string[];
    maxFileCount?: number;
    isShowAnswerBlock?: boolean;
    isShowApproveBlock?: boolean;
}

const initialValues: InquiryInfoNoteData = {
    note: '',
};

const InquiryInfoPageChatForm = ({
    inquiryId,
    noteMaxLength,
    setIsShowLoader,
    translationInquiryInfo,
    allowedFileExtensions,
    isShowApproveBlock,
    isShowAnswerBlock,
    maxFileCount,
}: IInquiryInfoPageApproveForm) => {
    const { open: openErrorModal } = useModalOpenError();

    const [scrollToFiles, setScrollToFiles] = useState(false);

    const [fileErrors, setFileErrors] = useState<TFileError[]>([]);

    const [newUploadedFiles, setNewUploadedFiles] = useState<IInquiryInfoNewFile[]>([]);

    const buttonsBlockRef = useRef<HTMLDivElement | null>(null);
    const attachedFilesLengthRef = useRef(0);

    const fileLimits = useSelector(makeSelectAuthSystemFileUploadLimits());
    const tCommon = useSelector(makeSelectTranslationCommon());

    const [postInquiryAnswer] = useLazyPostInquiryAnswerQuery();
    const [getInquiryInfoGeneral] = useLazyGetInquiryInfoGeneralQuery();
    const [attachFile, { isLoading: attachIsLoading }] = usePostInquiryAttachFileMutation();
    const [detachFile] = useDeleteInquiryDetachFileMutation();

    useEffect(() => {
        if (buttonsBlockRef.current && scrollToFiles) {
            buttonsBlockRef.current.scrollIntoView();
            setScrollToFiles(false);
        }
    }, [newUploadedFiles.length, scrollToFiles]);

    const validateFile = (file: File) => {
        if (
            allowedFileExtensions?.length &&
            !allowedFileExtensions.includes(getFileExtension(file.name).toUpperCase())
        ) {
            return EFormValidation.fileFormatIncorrect;
        } else if (file.size > fileLimits.maxFileSize) {
            return EFormValidation.fileSizeExceeded;
        } else if (file.name.length > fileLimits.maxFileNameLength) {
            return EFormValidation.fileNameTooLong;
        } else if (newUploadedFiles.some((el) => el.file.name === file.name)) {
            return EFormValidation.fileExist;
        }
    };

    const onFileDetachHandler = useCallback(
        (fileId: IInquiryInfoNewFile['fileId']) => {
            detachFile(fileId).then(() => {
                attachedFilesLengthRef.current--;
                setNewUploadedFiles((prev) => prev.filter((f) => f.fileId !== fileId));
            });
        },
        [detachFile],
    );

    const onFileAttachHandler = (attachedFiles: File[]) => {
        setFileErrors([]);
        const maxFileLength = maxFileCount ? maxFileCount : 1;
        if (attachedFiles) {
            if (attachedFiles.length <= maxFileLength - attachedFilesLengthRef.current) {
                attachedFiles.forEach((file) => {
                    const error = validateFile(file);
                    if (error) {
                        setFileErrors((prev) => [...prev, { file: file, error }]);
                    } else {
                        attachedFilesLengthRef.current++;
                        attachFile({
                            file: file,
                        }).then((item) => {
                            if (isSuccessFileAttach(item)) {
                                setNewUploadedFiles((prev) => [...prev, { file, fileId: item.data.fileId, inquiryId }]);
                            }
                            if (isFailedFileAttach(item)) {
                                setFileErrors((prev) => [
                                    ...prev,
                                    { file, error: item.error.code as TFileError['error'] },
                                ]);
                            }
                            setScrollToFiles(true);
                        });
                    }
                });
            } else {
                openErrorModal(
                    '',
                    getGlobalInputErrorModalTitle(
                        tCommon.form,
                        attachedFiles.length + attachedFilesLengthRef.current,
                        maxFileLength,
                    ),
                );
            }
        }

        setScrollToFiles(true);
    };

    const { getInputProps, open } = useDropzone({
        onDrop: onFileAttachHandler,
        multiple: true,
        noClick: false,
        noKeyboard: false,
    });

    const onAnswerHandler = (note: string, url: string) => {
        setIsShowLoader(true);
        postInquiryAnswer({
            body: { note },
            inquiryId: inquiryId,
            url: url,
        })
            .unwrap()
            .then(() => {
                getInquiryInfoGeneral({ inquiryId });
            })
            .catch(() => {
                openErrorModal('', translationInquiryInfo.chatHistory.errorMessage);
            })
            .finally(() => {
                setIsShowLoader(false);
                setNewUploadedFiles([]);
            });
    };

    const onSubmit = useCallback(
        (result: InquiryInfoNoteData) => {
            setIsShowLoader(true);
            postInquiryAnswer({
                inquiryId: inquiryId,
                body: {
                    note: result.note,
                },
                url: 'note',
            }).then(() => {
                getInquiryInfoGeneral({ inquiryId });
                setIsShowLoader(false);
                setNewUploadedFiles([]);
            });
        },
        [getInquiryInfoGeneral, inquiryId, postInquiryAnswer, setIsShowLoader],
    );

    if (!isShowAnswerBlock && !isShowApproveBlock) return null;

    return (
        <InquiryInfoPageFormContainer>
            <Loader showLoader={attachIsLoading}>
                {/* Hidden input */}
                <input {...getInputProps()} />
                <Formik initialValues={initialValues} onSubmit={onSubmit}>
                    {({ getFieldProps, handleSubmit, isValid }: FormikProps<InquiryInfoNoteData>) => (
                        <form onSubmit={handleSubmit}>
                            <InquiryInfoAnswerNoteStyled>
                                <FormFieldTextarea
                                    dataName="inquiry-chat-answer-block"
                                    maxLength={noteMaxLength}
                                    name={EInquiryChatNoteData.note}
                                    rows={4}
                                />
                            </InquiryInfoAnswerNoteStyled>
                            <InquiryInfoPageChatFormAttachedFiles
                                newUploadedFiles={newUploadedFiles}
                                onFileDetachHandler={onFileDetachHandler}
                            />
                            {!!fileErrors.length &&
                                fileErrors.map(({ file, error }) => (
                                    <ErrorItem key={file.name}>
                                        {`${file.name} - ${getAttachFileErrorMessage(
                                            tCommon,
                                            error,
                                            fileLimits,
                                            allowedFileExtensions,
                                        )}`}
                                    </ErrorItem>
                                ))}
                            <InquiryInfoPageChatHistoryAttachButtonBlockStyled>
                                <LinkBlock dataName="inquiry-info__answer-attach-file" onClick={open}>
                                    {translationInquiryInfo.chatHistory.approveBlock.attachFile}
                                </LinkBlock>
                                {!!maxFileCount && (
                                    <Typography component="span" fontSize={fontSizeSmall} themeColor="grey">
                                        {stringFormatParams(tCommon.form.maxFilesLength, {
                                            count: maxFileCount,
                                        })}
                                    </Typography>
                                )}
                            </InquiryInfoPageChatHistoryAttachButtonBlockStyled>
                            <InquiryInfoAnswerFooterStyled ref={buttonsBlockRef}>
                                {isShowApproveBlock && (
                                    <>
                                        <ButtonServiceUserDenied
                                            disabled={!isValid}
                                            kind="medium"
                                            onClick={() =>
                                                onAnswerHandler(
                                                    getFieldProps(EInquiryChatNoteData.note).value,
                                                    'approve',
                                                )
                                            }
                                        >
                                            {translationInquiryInfo.chatHistory.approveBlock.confirm}
                                        </ButtonServiceUserDenied>
                                        <ButtonServiceUserDenied
                                            className="inquiry-info__answer-attach-file"
                                            disabled={!isValid || !getFieldProps(EInquiryChatNoteData.note).value}
                                            kind="medium"
                                            onClick={() =>
                                                onAnswerHandler(
                                                    getFieldProps(EInquiryChatNoteData.note).value,
                                                    'reject',
                                                )
                                            }
                                        >
                                            {translationInquiryInfo.chatHistory.approveBlock.reject}
                                        </ButtonServiceUserDenied>
                                    </>
                                )}
                                {isShowAnswerBlock && (
                                    <ButtonServiceUserDenied
                                        disabled={!isValid || !getFieldProps(EInquiryChatNoteData.note).value}
                                        kind="medium"
                                        type="submit"
                                    >
                                        {translationInquiryInfo.chatHistory.answerBlock.sendAnswer}
                                    </ButtonServiceUserDenied>
                                )}
                            </InquiryInfoAnswerFooterStyled>
                        </form>
                    )}
                </Formik>
            </Loader>
        </InquiryInfoPageFormContainer>
    );
};

export default InquiryInfoPageChatForm;
